import {inject, Injectable} from "@angular/core";
import {map, Observable} from "rxjs";
import {GameService} from "./game.service";
import {PermissionsService} from "./permissions.service";
import {ActiveUserService} from "./active-user.service";
import {NavScope} from "../enum/NavScope";
import {ADMIN_NAV} from "../consts/AdminNavigation";
import {INavItem, NavBadge, NavExternalLink} from "../interfaces/INavItem";

/** A nav item resolved for the current scope/user, ready to render. */
export interface IRenderedNavItem {
  label: string;
  icon: string;
  /** Absolute routerLink segments, or undefined for external-link items. */
  routerLink?: (string | undefined)[];
  externalLink?: NavExternalLink;
  badge?: NavBadge;
  /** Legacy data-testid resolved for the current scope (may be undefined). */
  testId?: string;
}

/** A domain group with its visible items resolved for the current scope/user. */
export interface IRenderedNavGroup {
  group: string;
  icon: string;
  isFooter: boolean;
  items: IRenderedNavItem[];
}

/**
 * Turns the declarative ADMIN_NAV model (consts/AdminNavigation.ts) into the
 * concrete list of groups/items to show, given the current scope, active game and
 * the signed-in admin's permissions.
 *
 * The filtering is deliberately pure and synchronous so it can be unit-tested
 * without the DOM: pass a scope + gameId + drawnManually flag and assert the
 * resulting groups. Permission checks reuse the existing PermissionsService, so
 * the same rules gate the nav as gate the routes.
 */
@Injectable({providedIn: 'root'})
export class NavScopeService {
  private gameService = inject(GameService);
  private permissions = inject(PermissionsService);
  private activeUserService = inject(ActiveUserService);

  /** Matches a leading :gameId URL segment (a GUID). */
  private static readonly GUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /** Emits the current scope, derived from whether a game is selected. */
  public scope$: Observable<NavScope> =
    this.gameService.selectActiveGameId().pipe(map(id => this.scopeForGameId(id)));

  public scopeForGameId(gameId: string | null | undefined): NavScope {
    return gameId ? NavScope.GameOnly : NavScope.GlobalOnly;
  }

  /** Whether the item is available in the given scope (ignoring permissions/conditions). */
  public matchesScope(item: INavItem, scope: NavScope): boolean {
    if (item.scope === NavScope.Both) {
      return true;
    }
    return item.scope === scope;
  }

  /** Whether the signed-in admin is allowed to see the item in the given scope. */
  public hasAccess(item: INavItem, scope: NavScope): boolean {
    if (item.causableAdminOnly) {
      return this.activeUserService.activeUser().CausableAdmin;
    }

    // In global scope, prefer the scope-specific permission where one is declared
    // (e.g. Player Search: ViewGlobalPlayerSearch globally vs ViewPlayerInfo per-game).
    const permission = (scope === NavScope.GlobalOnly && item.globalPermission !== undefined)
      ? item.globalPermission
      : item.permission;

    if (permission === undefined) {
      return true;
    }
    return this.permissions.userHasPermission(permission);
  }

  /** Whether an extra runtime condition (e.g. manual draw) is satisfied. Game scope only. */
  public meetsCondition(item: INavItem, scope: NavScope, gameDrawnManually: boolean): boolean {
    if (scope !== NavScope.GameOnly || item.gameCondition === undefined) {
      return true;
    }
    if (item.gameCondition === 'drawnManually') {
      return gameDrawnManually;
    }
    return true;
  }

  public isVisible(item: INavItem, scope: NavScope, gameDrawnManually: boolean): boolean {
    return this.matchesScope(item, scope)
      && this.meetsCondition(item, scope, gameDrawnManually)
      && this.hasAccess(item, scope);
  }

  /**
   * Absolute routerLink for the item in the given scope. In game scope a per-game
   * route is used when present, otherwise it falls back to the flat global route
   * (some screens have only one flat route). Returns undefined for external links.
   */
  public routerLinkFor(item: INavItem, scope: NavScope, gameId: string | null | undefined)
    : (string | undefined)[] | undefined {
    if (item.externalLink) {
      return undefined;
    }
    if (scope === NavScope.GameOnly && item.gameRoute) {
      return ['/', gameId ?? '', item.gameRoute];
    }
    return ['/', item.globalRoute];
  }

  /**
   * Resolve the task-domain and page label for a URL from the nav model, so the page
   * header can show a "Domain / Page" breadcrumb. A leading :gameId segment is ignored.
   * Returns an empty object for URLs that aren't a top-level nav page (e.g. detail
   * routes like charity/:id) — the caller falls back to the route title.
   */
  public resolvePage(url: string): { domain?: string; title?: string } {
    const item = this.navItemForUrl(url);

    if (!item) {
      return {};
    }

    const group = ADMIN_NAV.find(groupP => groupP.items.includes(item));

    return group ? {domain: group.group, title: item.label} : {};
  }

  /** The nav item a URL is showing, ignoring any leading :gameId segment. */
  public navItemForUrl(url: string): INavItem | undefined {
    const path = url.split('?')[0].split('#')[0].replace(/^\/+/, '');
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return undefined;
    }
    // Drop a leading game id so /:gameId/manage-game resolves the same as /manage-game.
    const page_segments = NavScopeService.GUID.test(segments[0]) ? segments.slice(1) : segments;
    const page_route = page_segments[0];
    if (!page_route) {
      return undefined;
    }
    for (const group of ADMIN_NAV) {
      for (const item of group.items) {
        if (item.gameRoute === page_route || item.globalRoute === page_route) {
          return item;
        }
      }
    }
    return undefined;
  }

  /// The same page in `targetScopeP`, or undefined when there is no counterpart, the admin's permissions
  /// differ across scopes, or the item has a runtime condition that needs the game being switched to.
  public counterpartRouteForUrl(url: string, targetScopeP: NavScope): string | undefined {
    const item = this.navItemForUrl(url);

    if (!item || item.externalLink || item.gameCondition) {
      return undefined;
    }

    if (!this.matchesScope(item, targetScopeP) || !this.hasAccess(item, targetScopeP)) {
      return undefined;
    }

    return targetScopeP === NavScope.GameOnly ? item.gameRoute : item.globalRoute;
  }

  /** The legacy data-testid to apply for the item in the given scope, if any. */
  public testIdFor(item: INavItem, scope: NavScope): string | undefined {
    if (scope === NavScope.GameOnly) {
      return item.gameTestId ?? item.testId;
    }
    return item.globalTestId ?? item.testId;
  }

  /** Build the full set of visible, route-resolved groups for the current state. */
  public buildVisibleGroups(scope: NavScope, gameId: string | null | undefined, gameDrawnManually: boolean)
    : IRenderedNavGroup[] {
    return ADMIN_NAV
      .map(group => ({
        group: group.group,
        icon: group.icon,
        isFooter: group.isFooter ?? false,
        items: group.items
          .filter(item => this.isVisible(item, scope, gameDrawnManually))
          .map(item => ({
            label: item.label,
            icon: item.icon,
            routerLink: this.routerLinkFor(item, scope, gameId),
            externalLink: item.externalLink,
            badge: item.badge,
            testId: this.testIdFor(item, scope),
          })),
      }))
      .filter(group => group.items.length > 0);
  }
}
